import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./ModalImageViewer.css";

/**
 * Generic Modal Image Viewer Component
 * Displays images in a responsive modal with navigation, zoom, and keyboard controls
 * @param {boolean} show - Whether to show the modal
 * @param {string[]} images - Array of image URLs to display
 * @param {number} startIndex - Starting image index (default: 0)
 * @param {function} onHide - Callback when modal is closed
 * @param {string} title - Optional title for the modal
 */
export default function ModalImageViewer({
  show,
  images = [],
  startIndex = 0,
  onHide,
  title = "Image Viewer"
}) {
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(1);

  const list = Array.isArray(images) ? images.filter(url => url && typeof url === 'string') : [];
  const hasMultiple = list.length > 1;

  useEffect(() => {
    if (show && list.length > 0) {
      const i = Math.min(Math.max(0, startIndex), list.length - 1);
      setCurrent(i);
      setZoom(1);
    }
  }, [show, startIndex, list.length]);

  const goPrev = useCallback(() => {
    if (list.length < 2) return;
    setCurrent((c) => (c - 1 + list.length) % list.length);
    setZoom(1);
  }, [list.length]);

  const goNext = useCallback(() => {
    if (list.length < 2) return;
    setCurrent((c) => (c + 1) % list.length);
    setZoom(1);
  }, [list.length]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") onHide();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, goPrev, goNext, onHide]);

  if (!list.length) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      fullscreen="lg-down"
      dialogClassName="modal-image-viewer-dialog"
      contentClassName="modal-image-viewer-content"
    >
      <Modal.Header
        closeButton
        closeVariant="dark"
        className="modal-image-viewer-header"
      >
        <Modal.Title as="div" className="w-100 mb-0">
          <div className="modal-image-viewer-title-wrap">
            <p className="title-text mb-0">
              <i className="fas fa-images" aria-hidden />
              {title}
            </p>
            {hasMultiple && (
              <span className="modal-image-viewer-count">
                {current + 1} / {list.length}
              </span>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-image-viewer-stage d-flex align-items-center justify-content-center position-relative p-0">
        {hasMultiple && (
          <button
            type="button"
            className="btn modal-image-viewer-nav-btn position-absolute start-0 top-50 translate-middle-y ms-2 ms-sm-3"
            onClick={goPrev}
            aria-label="Previous image"
          >
            <i className="fas fa-chevron-left" />
          </button>
        )}
        <div className="modal-image-viewer-img-wrap mx-2 mx-sm-0">
          <img
            src={list[current]}
            alt={`${title} ${current + 1}`}
            style={{
              maxWidth: "100%",
              maxHeight: "76vh",
              objectFit: "contain",
              transform: `scale(${zoom})`,
              transformOrigin: "center center",
              transition: "transform 0.12s ease-out",
            }}
            draggable={false}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://placehold.co/400x300?text=Not+Found";
            }}
          />
        </div>
        {hasMultiple && (
          <button
            type="button"
            className="btn modal-image-viewer-nav-btn position-absolute end-0 top-50 translate-middle-y me-2 me-sm-3"
            onClick={goNext}
            aria-label="Next image"
          >
            <i className="fas fa-chevron-right" />
          </button>
        )}
      </Modal.Body>
      <Modal.Footer className="modal-image-viewer-footer">
        <div className="modal-image-viewer-toolbar">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="modal-image-viewer-zoom-label">Zoom</span>
            <div
              className="modal-image-viewer-zoom-group"
              role="group"
              aria-label="Zoom controls"
            >
              <button
                type="button"
                className="zoom-icon-btn"
                title="Zoom out"
                onClick={() =>
                  setZoom((z) =>
                    Math.max(0.5, Math.round((z - 0.25) * 100) / 100)
                  )
                }
              >
                <i className="fas fa-minus" aria-hidden />
              </button>
              <button type="button" onClick={() => setZoom(1)}>
                Reset
              </button>
              <button
                type="button"
                className="zoom-icon-btn"
                title="Zoom in"
                onClick={() =>
                  setZoom((z) =>
                    Math.min(3, Math.round((z + 0.25) * 100) / 100)
                  )
                }
              >
                <i className="fas fa-plus" aria-hidden />
              </button>
            </div>
          </div>
          <p className="modal-image-viewer-hint mb-0">
            {hasMultiple && <><kbd>←</kbd> <kbd>→</kbd> images · </>}
            <kbd>Esc</kbd> close
          </p>
        </div>
      </Modal.Footer>
    </Modal>
  );
}